/**
 * ReplyComposer — the SES email reply form for a feedback message
 * (STORY-010-005). Subject + plain-text Body + a live, safe text Preview of
 * what the recipient will read. "Send" is disabled when the feedback carries
 * no contact email (with a tooltip) — the server is the authoritative gate.
 *
 * The body is sent as plain text; the server (nh3) builds + sanitizes the HTML
 * part. We deliberately do NOT render admin HTML via dangerouslySetInnerHTML
 * (GUARDRAILS) — the preview is escaped text, which is XSS-safe by construction.
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@garden/ui";
import { ADMIN_NS } from "@/features/admin/api";

const MAX_SUBJECT = 200;
const MAX_BODY = 10_000;

export function ReplyComposer({
  canSend,
  pending,
  defaultSubject,
  onSend,
}: {
  canSend: boolean;
  pending: boolean;
  defaultSubject: string;
  onSend: (subject: string, bodyText: string) => void;
}): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState("");

  const trimmedSubject = subject.trim();
  const trimmedBody = body.trim();
  const formValid = trimmedSubject.length > 0 && trimmedBody.length > 0;
  const sendDisabled = !canSend || !formValid || pending;

  return (
    <section data-testid="reply-composer" className="flex flex-col gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
        {t("feedback.reply.title")}
      </h3>

      <div className="flex flex-col gap-1">
        <label htmlFor="reply-subject" className="text-sm text-[var(--g-secondary)]">
          {t("feedback.reply.subject")}
        </label>
        <input
          id="reply-subject"
          data-testid="reply-subject"
          type="text"
          value={subject}
          maxLength={MAX_SUBJECT}
          onChange={(e) => setSubject(e.target.value)}
          className="min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="reply-body" className="text-sm text-[var(--g-secondary)]">
          {t("feedback.reply.body")}
        </label>
        <textarea
          id="reply-body"
          data-testid="reply-body"
          value={body}
          rows={5}
          maxLength={MAX_BODY}
          onChange={(e) => setBody(e.target.value)}
          className="w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
        />
      </div>

      {/* Live preview — escaped text (XSS-safe); mirrors what SES will send. */}
      <div className="flex flex-col gap-1">
        <span className="text-sm text-[var(--g-secondary)]">
          {t("feedback.reply.preview")}
        </span>
        <div
          data-testid="reply-preview"
          className="min-h-[3rem] whitespace-pre-wrap rounded-[var(--g-radius-sm)] bg-[var(--g-elevated)] p-2 text-sm"
        >
          {body || <span className="text-[var(--g-secondary)]">{t("feedback.reply.previewEmpty")}</span>}
        </div>
      </div>

      <div className="flex justify-end" title={canSend ? undefined : t("feedback.reply.noEmailTooltip")}>
        <Button
          variant="primary"
          size="sm"
          data-testid="reply-send"
          disabled={sendDisabled}
          aria-label={t("feedback.reply.send")}
          onClick={() => onSend(trimmedSubject, trimmedBody)}
        >
          {pending ? t("feedback.reply.sending") : t("feedback.reply.send")}
        </Button>
      </div>
    </section>
  );
}

export default ReplyComposer;
